/**
 * Lookups the Instagram editor uses before a Reel is published: licensed audio
 * (`GET /ig_audio`) and places (Pages search). Pure parsing and error wording so
 * it has a Vitest gate; `supabase/functions/instagram-publish-lookup` calls it.
 *
 * https://developers.facebook.com/documentation/instagram-platform/content-publishing/audio-api
 */

export interface InstagramAudioResult {
  id: string;
  title: string;
  artist: string | null;
  kind: 'music' | 'original_sound';
  durationMs: number | null;
  artworkUrl: string | null;
  /** Instagram-hosted preview link; Meta says it may be empty. */
  previewUrl: string | null;
}

export interface InstagramPlaceResult {
  id: string;
  name: string;
  detail: string | null;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);

export function normalizeInstagramAudio(body: unknown): InstagramAudioResult[] {
  const b = (body ?? {}) as { audio?: unknown; data?: unknown };
  const list = Array.isArray(b.audio) ? b.audio : Array.isArray(b.data) ? b.data : [];
  const out: InstagramAudioResult[] = [];
  for (const raw of list) {
    const item = (raw ?? {}) as Record<string, unknown>;
    const id =
      str(item.audio_id) ?? (typeof item.audio_id === 'number' ? String(item.audio_id) : null);
    const title = str(item.title);
    if (!id || !title) continue;
    const kind = item.audio_type === 'original_sound' ? 'original_sound' : 'music';
    const duration = Number(item.duration_in_ms);
    out.push({
      id,
      title,
      artist: kind === 'music' ? str(item.display_artist) : str(item.ig_username),
      kind,
      durationMs: Number.isFinite(duration) && duration > 0 ? duration : null,
      artworkUrl: str(item.cover_artwork_thumbnail_uri) ?? str(item.cover_artwork_thumbnail_url),
      previewUrl: str(item.on_platform_audio_preview_link),
    });
  }
  return out;
}

export function normalizeInstagramPlaces(body: unknown): InstagramPlaceResult[] {
  const list = Array.isArray((body as { data?: unknown })?.data)
    ? ((body as { data: unknown[] }).data as unknown[])
    : [];
  const out: InstagramPlaceResult[] = [];
  for (const raw of list) {
    const item = (raw ?? {}) as Record<string, unknown>;
    const id = str(item.id);
    const name = str(item.name);
    if (!id || !name) continue;
    const loc = (item.location ?? {}) as Record<string, unknown>;
    const detail = [str(loc.city), str(loc.state), str(loc.country)].filter(Boolean).join(', ');
    out.push({ id, name, detail: detail || null });
  }
  return out;
}

export type InstagramLookupAvailability =
  { state: 'ok' } | { state: 'unavailable'; message: string };

/**
 * Whether a failed Graph response means "this connection cannot use the feature"
 * (permission, app review, wrong login type) rather than a passing error.
 */
export function classifyInstagramLookupError(
  status: number,
  body: unknown,
  feature: 'audio' | 'location',
): InstagramLookupAvailability | { state: 'error'; message: string } {
  const error = ((body ?? {}) as { error?: { code?: number; message?: string } }).error;
  const code = error?.code;
  const noun = feature === 'audio' ? 'Instagram’s music library' : 'place search';
  // Meta answers place search with code 100 when the app lacks Page Public Metadata Access, so for this endpoint it is
  // a permission answer, seen live on 4 Nov 2026, and retrying will not change it.
  const placeSearchRefused = feature === 'location' && code === 100;
  if (placeSearchRefused || code === 10 || code === 200 || code === 3 || status === 403) {
    return {
      state: 'unavailable',
      message: `This Instagram connection cannot use ${noun}. ${
        feature === 'audio'
          ? 'Music needs an Instagram professional account connected through Facebook.'
          : 'Place search needs extra Facebook permission. You can still paste a Facebook Page id for the place.'
      }`,
    };
  }
  if (code === 190 || status === 401) {
    return {
      state: 'error',
      message: 'The Instagram connection has expired. Reconnect it in Settings, then try again.',
    };
  }
  return {
    state: 'error',
    message:
      feature === 'audio'
        ? 'Instagram could not search its music library just now. Try again.'
        : 'Instagram could not search for places just now. Try again.',
  };
}

/** A track's length as m:ss for the picker. */
export function formatAudioDuration(ms: number | null): string {
  if (!ms) return '';
  const total = Math.round(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

export const INSTAGRAM_AUDIO_PREVIEW_NOTE =
  'Instagram does not allow previewing a Reel with attached music, so the preview here stays silent. The track plays once the Reel is live.';
